import {
  modifyPrivacyForm,
  waitForPrivacyForm,
} from '@/helpers/privacyModifiers';
import { useEffect, useRef } from 'react';
import ClientOnly from './ClientOnly';

function PrivacySettings() {
  const ref = useRef<HTMLDivElement>(null);

  // attached here instead of an inline onclick attribute, which a strict CSP
  // (no 'unsafe-inline') would block
  useEffect(() => {
    const button = ref.current?.querySelector(
      '#ppms_cm_privacy_settings_button'
    );
    if (!button) {
      return;
    }
    const onClick = (event: Event) => {
      event.preventDefault();
      // defined globally in public/assets/accessibility.js
      (
        window as Window & { watchOutForConsentModifierClick?: () => void }
      ).watchOutForConsentModifierClick?.();
    };
    button.addEventListener('click', onClick);
    return () => button.removeEventListener('click', onClick);
  }, []);

  return (
    <div ref={ref} dangerouslySetInnerHTML={{ __html: privacySettings() }} />
  );
}

export default function ScriptIntegration({
  scriptVariant,
}: {
  scriptVariant?: 'privacySettings' | 'privacyForm';
}) {
  switch (scriptVariant) {
    case 'privacySettings':
      return (
        <ClientOnly>
          <PrivacySettings />
        </ClientOnly>
      );

    case 'privacyForm':
      if (typeof window !== 'undefined') {
        waitForPrivacyForm();
        modifyPrivacyForm();
      }

      return (
        <ClientOnly>
          <div
            role="region"
            id="privacyForm"
            aria-live="polite"
            aria-label="Formular zur Datenschutzerklärung"
            dangerouslySetInnerHTML={{
              __html: privacyForm(
                typeof window !== 'undefined' &&
                  process.env.NEXT_PUBLIC_PRODUCTION_INSTANCE === 'true'
                  ? {
                      id1: 'f96d4dd0-7e69-4f72-a232-77f132dafffd',
                      id2: 'bc25c46a-bfc5-426a-b5be-271175438fd3',
                      id3: '17a22905-8149-4395-ac93-3fab7f6d0fd7',
                      id4: 'ffb6bd6c-3b74-45da-9662-0941885e5b69',
                    }
                  : {
                      id1: '1c707359-0b70-4bf2-b601-9c7a6ef35831',
                      id2: '4dda5b44-2a7e-4d82-ba1e-d5a39a3f1fa7',
                      id3: '245dbe9d-110b-45e3-85d6-4ac2639b5a82',
                      id4: '3fd09143-850d-44c5-95a2-5204650f07f7',
                    }
              ),
            }}
          />
        </ClientOnly>
      );

    default:
      return null;
  }
}

function privacySettings() {
  return `<div
  id="ppms_cm_privacy_settings"
  class="ppms_cm_privacy_settings_widget"
  style="display:block; min-width:initial !important;"
  data-editor-centralize="true"
  data-main-container="true"
  data-root="true"
>
  <div
    class="ppms_cm_privacy_settings_widget_content"
    data-disable-select="true"
    id="ppms-6f83af1f-fdef-441d-8985-66402907faa2"
  >
    <h2
      class="ppms_cm_privacy_settings_form_link_header"
      id="ppms_cm_privacy_settings_form_link_header_id"
    >
      Datenschutzeinstellungen
    </h2>
    <p
      class="ppms_cm_privacy_settings_form_link_text"
      id="ppms_cm_privacy_settings_form_link_text_id"
    >
      Verwalten Sie hier Ihre Einstellungen zum Datenschutz auf der Webseite
      LOTTO.de. Wir erfassen Daten, um Ihre Erfahrung auf unserer Website stetig
      zu verbessern. Wir sammeln und nutzen Daten nur für Zwecke, für die eine
      Einwilligung vorliegt.
    </p>
    <button
      class="ppms_cm_privacy_settings_button_show"
      id="ppms_cm_privacy_settings_button"
      tabindex="0"
    >
      Einstellungen bearbeiten
    </button>
  </div>
</div>`;
}

function privacyForm({ id1, id2, id3, id4 }) {
  return `<div
  id="ppms_cm_data_subject"
  class="ppms_cm_data_subject_widget__wrapper"
  style="min-width: auto !important"
  data-editor-centralize="true"
  data-main-container="true"
  data-root="true"
>
  <h2
    id="ppms_cm_data_subject_header"
    class="ppms_cm_data_subject_form_link_header"
  >
    Datenschutz-Anfragen
  </h2>
  <p id="ppms_cm_data_subject_paragraph" class="paragraph">
    Bitte wählen Sie im Folgenden die Art der Datenschutz-Anfrage und fügen Sie
    eventuelle besondere Anfragen in den Nachrichtentext ein. Wir werden unser
    Bestes tun, um Ihre Anfrage schnellstmöglich zu bearbeiten. Wir benötigen
    Ihre E-Mail-Adresse, um Sie bezüglich Ihrer Anfrage zu kontaktieren. Wir
    werden Ihre E-Mail-Adresse für keinen anderen Zweck außer der Bearbeitung
    Ihrer Anfrage verwenden. Formularfelder mit einem * sind Pflichtfelder.
  </p>

  <form
    id="ppms_cm_data_subject_form"
    class="ppms_cm_data_subject_form"
    data-disable-select="true"
  >
    <label
      id="ppms_cm_data_subject_form_label"
      for="ppms_cm_data_subject_form_input"
      class="ppms_cm_data_subject_form_label"
      >E-Mail <span class="">*</span>
    </label>
    <input
      type="email"
      name="email"
      required
      id="ppms_cm_data_subject_form_input"
      class="ppms_cm_data_subject_form_input"
      tabindex="0"
    />
    <label
      id="ppms_cm_data_subject_form_label_request_type"
      class="ppms_cm_data_subject_form_label"
      for="ppms_cm_data_subject_form_type_select"
      >Art der Anfrage <span class="">*</span></label
    >
    <div
      id="ppms_cm_data_subject_form_select_wrapper"
      name="event"
      class="ppms_cm_data_subject_form_select_wrapper"
      data-disable-select="true"
      data-select-name="event"
      aria-label="Wählen Sie den Typ der Anfrage"
      role="combobox"
      aria-labelledby="ppms_cm_data_subject_form_label_request_type"
      aria-controls="ppms_cm_data_subject_form_select_extendable"
      aria-expanded="false"
    >
      <div
        id="ppms_cm_data_subject_form_select_btn"
        data-type="customSelect"
        class="ppms_select_component_btn"
        tabindex="0"
        aria-haspopup="listbox"
      >
        <span id="${id1}" aria-live="polite">Recht auf Auskunft</span>
        <span class="ppms_select_component-btn-chevron" id="${id2}">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            id="${id3}"
          >
            <path
              fill="#131313"
              d="M8.2,8.7l2.8-2.8l1.4,1.4l-2.8,2.8l-1.4,1.4L4,7.2l1.4-1.4L8.2,8.7z"
              id="${id4}"
            ></path>
          </svg>
        </span>
      </div>
      <div
        id="ppms_cm_data_subject_form_select_extendable"
        class="ppms_select_component_extendable"
        data-disable-select="true"
        data-type="customSelectWrapper"
        aria-hidden="true"
      >
        <div
          id="ppms_cm_data_subject_form_select_extended_item_1"
          class="ppms_select_component_extended_item"
          data-type="customOption"
          data-value="view_data"
          tabindex="-1"
        >
          Auskunft zu personenbezogenen Daten
        </div>
        <div
          id="ppms_cm_data_subject_form_select_extended_item_2"
          class="ppms_select_component_extended_item"
          data-type="customOption"
          data-value="delete_data"
          tabindex="-1"
        >
          Löschung personenbezogener Daten
        </div>
        <div
          id="ppms_cm_data_subject_form_select_extended_item_3"
          class="ppms_select_component_extended_item"
          data-type="customOption"
          data-value="change_data"
          tabindex="-1"
        >
          Berichtigung personenbezogener Daten
        </div>
      </div>
    </div>
    <label
      id="ppms_cm_data_subject_form_label_textarea"
      for="ppms_cm_data_subject_form_textarea"
      class="ppms_cm_data_subject_form_label"
      >Nachricht an den Webseiten-Administrator</label
    >
    <textarea
      name="message"
      rows="7"
      id="ppms_cm_data_subject_form_textarea"
      class="ppms_cm_data_subject_form_textarea"
      tabindex="0"
    ></textarea>
    <input
      type="submit"
      id="ppms_cm_data_subject_form_submit"
      class="ppms_cm_data_subject_form_submit"
      value="Anfrage senden"
      tabindex="0"
    />
  </form>
</div>`;
}
